import { MAX_BENEFIT_AGE_LIST } from "../../../../../../constants/maxBenefitAges";

export const MONTHLY_AMOUNT_MAX = 5000;

// Every column an age table can have. Columns with no amounts are skipped,
// so the older four-column tables work without being told which they are.
const AGE_COLUMNS = ["selfAmount", "spouseAmount", "childAmount", "allAmount"];

/**
 * Age-table amounts are held as ANNUAL CENTS: a monthly dollar amount x 12
 * months x 100 cents, the same x1200 formatMaxBenefit (UserContext) applies
 * to the single amounts. The tables display them divided by 1200. So the
 * $5,000-a-month cap is 6,000,000 here: $500 a month is 600,000.
 */
const AGE_TABLE_UNITS_PER_MONTHLY_DOLLAR = 1200;
const AGE_TABLE_MAX = MONTHLY_AMOUNT_MAX * AGE_TABLE_UNITS_PER_MONTHLY_DOLLAR;

export const AGE_TABLE_MESSAGE =
  "Amounts must stay the same or increase with age, and no employee amount " +
  "can be more than 3 times the age " +
  MAX_BENEFIT_AGE_LIST.MINIMUM_AGE +
  " amount.";

export const AGE_TABLE_MAX_MESSAGE = `No amount in the table can be more than $${MONTHLY_AMOUNT_MAX.toLocaleString()} a month.`;

const isBlank = (value) => value === null || value === undefined || value === "";

/** The method chosen: MANUAL, PCT_OF_METAL, ACA_MINIMUM, or null. */
const selectedKey = (choices) =>
  choices && typeof choices === "object"
    ? Object.keys(choices).find((key) => choices[key]) || null
    : null;

/**
 * EVERY RULE FOR THE AMOUNTS THE EMPLOYER TYPES IN, from the form values
 * alone. No side effects and nothing written back to the form, so it gives
 * the same answer wherever it's called: the red cells, the messages, and the
 * save check that Next and Save & Exit both run.
 *
 * Returns:
 *   cellErrors  which age-table cells are red. "row<age>" for the AGE table,
 *               "<column>_row<age>" for FAMILY_AGE (the keys each table
 *               already reads).
 *   ageMessage  the age-table message, or null.
 *   fieldErrors { monthlySelfAmount: "...", ... } for the $5,000 maximum.
 *   valid       true when there is nothing to fix.
 *
 * The rules:
 *   - Age tables: no column may go down as age goes up; blanks are skipped.
 *   - Age tables: the employee (self) column only, no amount more than 3x
 *     the smallest. Spouse and child columns may start at $0.
 *   - Age tables: the employee amount at the youngest age is required.
 *   - Age tables: every cell, every column, at most $5,000 a month.
 *   - Monthly amounts (one amount, or by family size): at most $5,000.
 */
export function checkAllowanceAmounts(values) {
  const cellErrors = {};
  const fieldErrors = {};
  let ageMessage = null;

  const method = selectedKey(values?.allowanceMethod);
  const benefit = values?.maxBenefit || {};
  const isManual = method === "MANUAL" || !method;

  if (isManual && (benefit.AGE || benefit.FAMILY_AGE)) {
    const minAge = MAX_BENEFIT_AGE_LIST.MINIMUM_AGE;
    const maxAge = MAX_BENEFIT_AGE_LIST.MAXIMUM_AGE;
    // The AGE table has the employee column only.
    const columns = benefit.AGE ? ["selfAmount"] : AGE_COLUMNS;
    const cellKey = (column, age) =>
      benefit.AGE ? `row${age}` : `${column}_row${age}`;
    let ageError = false;
    let overMax = false;

    for (const column of columns) {
      const rows = [];
      for (let age = minAge; age <= maxAge; age++) {
        const raw = values?.[column + age];
        if (!isBlank(raw)) rows.push({ age, amount: Number(raw) });
      }
      if (rows.length === 0) continue;

      // $5,000 a month, every column, every row.
      for (const row of rows) {
        if (row.amount > AGE_TABLE_MAX) {
          cellErrors[cellKey(column, row.age)] = true;
          overMax = true;
        }
      }

      // No decrease with age.
      for (let i = 1; i < rows.length; i++) {
        if (rows[i].amount < rows[i - 1].amount) {
          cellErrors[cellKey(column, rows[i].age)] = true;
          ageError = true;
        }
      }

      // 3x, employee column only. Flags the first row at the largest amount.
      if (column === "selfAmount") {
        const amounts = rows.map((row) => row.amount);
        const smallest = Math.min(...amounts);
        const largest = Math.max(...amounts);
        if (largest > smallest * 3) {
          const row = rows.find((r) => r.amount === largest);
          cellErrors[cellKey(column, row.age)] = true;
          ageError = true;
        }
      }
    }

    // The employee amount at the youngest age is required.
    if (isBlank(values?.["selfAmount" + minAge])) {
      ageError = true;
    }
    // One message per problem found, shown together under the table.
    const messages = [];
    if (ageError) messages.push(AGE_TABLE_MESSAGE);
    if (overMax) messages.push(AGE_TABLE_MAX_MESSAGE);
    ageMessage = messages.length ? messages.join(" ") : null;
  }

  // $5,000 monthly maximum, for the single and family-size amounts.
  if (isManual && !benefit.AGE && !benefit.FAMILY_AGE) {
    const tooHigh = `Monthly amount cannot exceed $${MONTHLY_AMOUNT_MAX.toLocaleString()}`;
    for (const field of [
      "monthlySelfAmount",
      "monthlyFirstDependentAmount",
      "monthlyAdditionalDependentAmount",
    ]) {
      const raw = values?.[field];
      if (!isBlank(raw) && Number(raw) > MONTHLY_AMOUNT_MAX) {
        fieldErrors[field] = tooHigh;
      }
    }
  }

  const valid = !ageMessage && Object.keys(fieldErrors).length === 0;
  return { cellErrors, ageMessage, fieldErrors, valid };
}